import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CornerDownLeft, MapPin, Route as RouteIcon, Search } from 'lucide-react';
import { getRouteDisplayName } from '../../utils/routeManagementHelpers';

// Global quick-search (⌘K / Ctrl+K). Searches saved stops and routes and jumps
// to them on the map.
export default function CommandPalette({ open, onClose, controller }) {
  const { stops, savedRoutes, viewStopsOnMap, viewRoutesOnMap, resolveStopMasterName } = controller;
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      const id = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const q = query.trim().toLowerCase();

  const stopResults = useMemo(() => {
    if (!q) return [];
    return stops
      .filter(
        (s) =>
          s.stopName.toLowerCase().includes(q) ||
          String(s.stopId || '').toLowerCase().includes(q)
      )
      .slice(0, 6);
  }, [q, stops]);

  const routeResults = useMemo(() => {
    if (!q) return [];
    return savedRoutes
      .filter(
        (r) =>
          getRouteDisplayName(r, resolveStopMasterName).toLowerCase().includes(q) ||
          String(r.routeNumber || '').toLowerCase().includes(q) ||
          String(r.direction || '').toLowerCase().includes(q)
      )
      .slice(0, 6);
  }, [q, savedRoutes, resolveStopMasterName]);

  if (!open) return null;

  const hasResults = stopResults.length > 0 || routeResults.length > 0;

  function pickStop(stop) {
    viewStopsOnMap([stop._id]);
    onClose();
  }

  function pickRoute(route) {
    viewRoutesOnMap([route]);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[1200] flex items-start justify-center pt-[12vh] px-4">
      <div
        className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-pop border border-slate-200 overflow-hidden animate-slide-up">
        <div className="flex items-center gap-2.5 px-4 h-12 border-b border-slate-100">
          <Search className="h-4 w-4 text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search stops and routes…"
            className="flex-1 h-full bg-transparent outline-none text-[14px] text-slate-900 placeholder:text-slate-400"
          />
          <kbd className="text-[10px] font-medium text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">Esc</kbd>
        </div>

        <div className="max-h-[52vh] overflow-y-auto py-2">
          {!q && (
            <p className="px-4 py-8 text-center text-[13px] text-slate-400">
              Start typing to find a stop or route.
            </p>
          )}

          {q && !hasResults && (
            <p className="px-4 py-8 text-center text-[13px] text-slate-400">
              No matches for “{query}”.
            </p>
          )}

          {stopResults.length > 0 && (
            <Group title="Stops">
              {stopResults.map((stop) => (
                <Row
                  key={stop._id}
                  icon={MapPin}
                  iconClass="text-brand-500"
                  title={stop.stopName}
                  meta={stop.stopId}
                  onClick={() => pickStop(stop)}
                />
              ))}
            </Group>
          )}

          {routeResults.length > 0 && (
            <Group title="Routes">
              {routeResults.map((route) => (
                <Row
                  key={route._id}
                  icon={RouteIcon}
                  iconClass="text-violet-500"
                  title={getRouteDisplayName(route, resolveStopMasterName)}
                  meta={`Route ${route.routeNumber} · ${route.direction}`}
                  onClick={() => pickRoute(route)}
                />
              ))}
            </Group>
          )}
        </div>
      </div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="px-2">
      <p className="px-2 pt-2 pb-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">
        {title}
      </p>
      {children}
    </div>
  );
}

function Row({ icon: Icon, iconClass, title, meta, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors text-left"
    >
      <span className="h-7 w-7 rounded-lg bg-slate-100 inline-flex items-center justify-center shrink-0">
        <Icon className={`h-3.5 w-3.5 ${iconClass}`} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-slate-900 truncate">{title}</p>
        {meta && <p className="text-[11.5px] text-slate-400 truncate">{meta}</p>}
      </div>
      <CornerDownLeft className="h-3.5 w-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </button>
  );
}
